import { Link } from 'react-router-dom';
import { HiTruck, HiRefresh, HiExclamation, HiClock, HiMail } from 'react-icons/hi';
import { trackContactClick } from '../utils/analytics';
import { STORE } from '../utils/store';
import '../styles/Policy.css';

/*
  Shipping & Returns.

  Same page shell as the cookie policy: one column, headings, prose,
  and a handful of shared blocks (label/value rows, side-by-side cards,
  numbered steps, callouts). The previous version put nearly every
  element in its own coloured box — heading boxes inside section boxes
  with a pink box for the sentence underneath — which on a phone left
  more frame than content.
*/

const ShippingReturns = () => (
  <section className="policy-page">
    <div className="policy-container">
      <header className="policy-head">
        <h1>Shipping &amp; Returns</h1>
        <p className="policy-updated">Last updated: 25 September 2026</p>
      </header>

      <div className="policy-body">
        <p className="policy-lead">
          Every order is packed by hand, insured, and tracked from our hands
          to yours. Here is how long it takes, and what to do if something is
          not right when it arrives.
        </p>

        {/* ---------- SHIPPING ---------- */}
        <h2>
          <HiTruck aria-hidden="true" />
          Shipping
        </h2>

        <dl className="policy-facts">
          <div className="policy-fact">
            <dt>Processing</dt>
            <dd>
              <strong>24–48 hours</strong> after payment is confirmed
            </dd>
          </div>
          <div className="policy-fact">
            <dt>Delivery</dt>
            <dd>
              <strong>3–7 business days</strong> anywhere in India
            </dd>
          </div>
          <div className="policy-fact">
            <dt>Special orders</dt>
            <dd>
              Custom energized gemstones take <strong>3–5 days</strong> longer
            </dd>
          </div>
        </dl>

        <h3>By location</h3>
        <dl className="policy-facts">
          <div className="policy-fact">
            <dt>Metro cities</dt>
            <dd>3–5 business days</dd>
          </div>
          <div className="policy-fact">
            <dt>Tier 2 &amp; 3 cities</dt>
            <dd>5–7 business days</dd>
          </div>
          <div className="policy-fact">
            <dt>Remote areas</dt>
            <dd>7–10 business days</dd>
          </div>
        </dl>

        <h3>What is included</h3>
        <ul className="policy-list">
          <li>
            <strong>Free shipping</strong> on every order, anywhere in India
          </li>
          <li>Tamper-proof, secure packaging</li>
          <li>A tracking number as soon as the parcel is dispatched</li>
          <li>Every shipment fully insured</li>
        </ul>

        <p className="policy-note">
          Delivery times can stretch with courier delays, weather or other
          circumstances outside our control. The tracking link will always
          show where the parcel is.
        </p>

        {/* ---------- RETURNS ---------- */}
        <h2>
          <HiRefresh aria-hidden="true" />
          Returns &amp; Exchanges
        </h2>
        <p>
          You have <strong>7 days from the day of delivery</strong> to return
          an item, provided it meets the conditions below.
        </p>

        <div className="policy-cards">
          <div className="policy-card">
            <h3 className="is-good">Can be returned</h3>
            <ul className="policy-list">
              <li>Unused, in its original packaging</li>
              <li>All tags and the lab certificate intact</li>
              <li>Approved by our support team before it is sent back</li>
            </ul>
          </div>
          <div className="policy-card">
            <h3 className="is-bad">Cannot be returned</h3>
            <ul className="policy-list">
              <li>Custom energized gemstones</li>
              <li>Personalized items</li>
              <li>Used or worn products</li>
            </ul>
          </div>
        </div>

        {/* ---------- DAMAGED ---------- */}
        <h2>
          <HiExclamation aria-hidden="true" />
          Damaged or Incorrect Items
        </h2>
        <p>
          If a parcel arrives damaged, defective or with the wrong item,
          please record a <strong>clear unboxing video</strong> before you
          open it. It is the one thing that lets us settle a claim quickly.
        </p>

        <h3>The video should</h3>
        <ul className="policy-list">
          <li>Show the sealed package before it is opened</li>
          <li>Run through the whole unboxing without cuts</li>
          <li>Show the condition of the product and any damage</li>
          <li>Be well lit and in focus</li>
        </ul>

        <div className="policy-callout is-warn">
          <p>
            <strong>Please note:</strong> a claim for a damaged item without a
            valid unboxing video may not be eligible for a return, replacement
            or refund.
          </p>
        </div>

        <h3>How we put it right</h3>
        <div className="policy-cards is-three">
          <div className="policy-card">
            <h3>Replacement</h3>
            <p>A new piece shipped straight away</p>
          </div>
          <div className="policy-card">
            <h3>Full refund</h3>
            <p>Every rupee back to the original payment method</p>
          </div>
          <div className="policy-card">
            <h3>Store credit</h3>
            <p>110% of the order value, for a future purchase</p>
          </div>
        </div>

        {/* ---------- PROCESS ---------- */}
        <h2>How to Return an Item</h2>
        <ol className="policy-steps">
          <li className="policy-step">
            <div>
              <h3>Contact support</h3>
              <p>Email us your order number and the reason for the return.</p>
            </div>
          </li>
          <li className="policy-step">
            <div>
              <h3>Wait for approval</h3>
              <p>We review every request and reply within 24 hours.</p>
            </div>
          </li>
          <li className="policy-step">
            <div>
              <h3>Ship it back</h3>
              <p>Pack it securely and send it to the address we give you.</p>
            </div>
          </li>
          <li className="policy-step">
            <div>
              <h3>Get your refund</h3>
              <p>Processed within 7 business days of the item passing inspection.</p>
            </div>
          </li>
        </ol>

        {/* ---------- REFUNDS ---------- */}
        <h2>
          <HiClock aria-hidden="true" />
          Refunds
        </h2>
        <ul className="policy-list">
          <li>
            Once a returned item is received and inspected, an eligible refund
            goes back to the original payment method within{' '}
            <strong>7 business days</strong>.
          </li>
          <li>
            The cost of posting an item back to us is yours, unless the return
            is due to an error on our part.
          </li>
        </ul>

        {/* ---------- CONTACT ---------- */}
        <h2>
          <HiMail aria-hidden="true" />
          Contact Support
        </h2>
        <p>For anything about shipping, a return or a damaged parcel:</p>
        <dl className="policy-facts">
          <div className="policy-fact">
            <dt>Email</dt>
            <dd>
              <a
                href={`mailto:${STORE.email}`}
                onClick={() => trackContactClick('email', 'shipping_returns')}
              >
                {STORE.email}
              </a>
            </dd>
          </div>
          <div className="policy-fact">
            <dt>Hours</dt>
            <dd>Monday to Saturday, 10 AM – 7 PM IST</dd>
          </div>
        </dl>
        <p className="policy-note">
          See also our <Link to="/terms-of-service">Terms of Service</Link> and{' '}
          <Link to="/privacy-policy">Privacy Policy</Link>.
        </p>

        <div className="policy-foot">
          <p>
            Your satisfaction is our priority. We want the whole experience,
            from order to unboxing, to be smooth and hassle-free.
          </p>
        </div>
      </div>
    </div>
  </section>
);

export default ShippingReturns;
